import { backendClient } from '../lib/api/backendClient';
import type { Album } from '../interfaces/albums';
import type { SimpleArtist } from '../interfaces/artist';

type FavoriteAlbumMetadata = {
  title?: string;
  primaryArtistNames?: string[];
  imageUrl?: string;
  totalTracks?: number;
  releaseDate?: string;
  [key: string]: unknown;
};

type FavoriteAlbumPayload = {
  id: string;
  userId: string;
  spotifyAlbumId: string;
  addedAt: string;
  metadata: FavoriteAlbumMetadata;
  createdAt?: string;
  updatedAt?: string;
};

type CachedAlbumsResponse = {
  albums?: FavoriteAlbumPayload[];
};

export type SyncAlbumsResponse = {
  totalFetched: number;
  totalUpserted: number;
  albums: FavoriteAlbumPayload[];
};

type AlbumsPageResponsePayload = {
  albums?: FavoriteAlbumPayload[];
  nextCursor?: string | null;
  overlap?: boolean;
  totalFetched?: number;
  sessionId?: string | null;
  commit?: boolean;
  persistedCount?: number;
  totalPersisted?: number | null;
  latestAlbumId?: string | null;
  latestAlbumAddedAt?: string | null;
};

export type FetchAlbumsPageResponse = {
  albums: Album[];
  nextCursor: string | null;
  overlap: boolean;
  totalFetched: number;
  sessionId: string | null;
  commit: boolean;
  persistedCount: number;
  totalPersisted: number | null;
  latestAlbumId: string | null;
  latestAlbumAddedAt: string | null;
};

const UNKNOWN_ALBUM_TITLE = 'Saved Album';
const UNKNOWN_ARTIST_NAME = 'Unknown Artist';

const coerceAlbumTitle = (metadata: FavoriteAlbumMetadata): string =>
  typeof metadata.title === 'string' && metadata.title.trim().length > 0
    ? metadata.title.trim()
    : UNKNOWN_ALBUM_TITLE;

const coerceReleaseDate = (metadata: FavoriteAlbumMetadata): string =>
  typeof metadata.releaseDate === 'string' && metadata.releaseDate.trim().length > 0
    ? metadata.releaseDate.trim()
    : '1900-01-01';

const coerceImageUrl = (metadata: FavoriteAlbumMetadata): string | undefined =>
  typeof metadata.imageUrl === 'string' && metadata.imageUrl.trim().length > 0
    ? metadata.imageUrl.trim()
    : undefined;

const coerceTotalTracks = (metadata: FavoriteAlbumMetadata): number =>
  typeof metadata.totalTracks === 'number' && Number.isFinite(metadata.totalTracks)
    ? metadata.totalTracks
    : 0;

const coerceArtistNames = (metadata: FavoriteAlbumMetadata): string[] =>
  Array.isArray(metadata.primaryArtistNames)
    ? metadata.primaryArtistNames
        .map((name) => (typeof name === 'string' ? name.trim() : ''))
        .filter((name) => name.length > 0)
    : [];

const buildFallbackArtistId = (albumId: string, artistName: string, index: number) =>
  `favorite-${albumId}-${index}-${artistName.replace(/\s+/g, '-').toLowerCase()}`;

const buildSimpleArtist = (albumId: string, artistName: string, index: number): SimpleArtist => {
  const normalizedName = artistName.trim() || UNKNOWN_ARTIST_NAME;
  const fallbackId = buildFallbackArtistId(albumId, normalizedName, index);
  return {
    id: fallbackId,
    name: normalizedName,
    type: 'artist',
    uri: `spotify:artist:${fallbackId}`,
    href: '',
    external_urls: { spotify: '' }
  };
};

const mergeMetadata = (favorite: FavoriteAlbumPayload): FavoriteAlbumMetadata => {
  const base = favorite.metadata ?? {};
  const merged: FavoriteAlbumMetadata = { ...base };

  if (!merged.title && typeof (favorite as any)?.name === 'string' && (favorite as any).name.trim()) {
    merged.title = (favorite as any).name.trim();
  }

  if (
    !merged.primaryArtistNames &&
    Array.isArray((favorite as any)?.primaryArtistNames) &&
    (favorite as any).primaryArtistNames.length
  ) {
    merged.primaryArtistNames = (favorite as any).primaryArtistNames
      .map((name: unknown) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name: string): name is string => name.length > 0);
  }

  if (!merged.imageUrl && typeof (favorite as any)?.imageUrl === 'string' && (favorite as any).imageUrl.trim()) {
    merged.imageUrl = (favorite as any).imageUrl.trim();
  }

  if (
    merged.totalTracks == null &&
    typeof (favorite as any)?.totalTracks === 'number' &&
    Number.isFinite((favorite as any).totalTracks)
  ) {
    merged.totalTracks = (favorite as any).totalTracks;
  }

  if (!merged.releaseDate && typeof (favorite as any)?.releaseDate === 'string' && (favorite as any).releaseDate.trim()) {
    merged.releaseDate = (favorite as any).releaseDate.trim();
  }

  return merged;
};

const toAlbumFromFavorite = (favorite: FavoriteAlbumPayload): Album => {
  const metadata = mergeMetadata(favorite);
  const albumId = favorite.spotifyAlbumId;
  const albumTitle = coerceAlbumTitle(metadata);
  const primaryArtistNames = coerceArtistNames(metadata);
  const artists: SimpleArtist[] = primaryArtistNames.length
    ? primaryArtistNames.map((name, index) => buildSimpleArtist(albumId, name, index))
    : [buildSimpleArtist(albumId, UNKNOWN_ARTIST_NAME, 0)];

  const imageUrl = coerceImageUrl(metadata);

  return {
    id: albumId,
    name: albumTitle,
    album_type: 'album',
    artists,
    available_markets: [],
    external_urls: { spotify: `https://open.spotify.com/album/${albumId}` },
    href: `https://api.spotify.com/v1/albums/${albumId}`,
    images: imageUrl
      ? [
          {
            url: imageUrl,
            width: 640,
            height: 640
          }
        ]
      : [],
    release_date: coerceReleaseDate(metadata),
    release_date_precision: 'day',
    total_tracks: coerceTotalTracks(metadata),
    type: 'album',
    uri: `spotify:album:${albumId}`
  };
};

const deserializeFavoriteAlbums = (payloads: FavoriteAlbumPayload[] | undefined): Album[] => {
  if (!Array.isArray(payloads) || payloads.length === 0) {
    return [];
  }
  return payloads
    .filter((payload): payload is FavoriteAlbumPayload => !!payload?.spotifyAlbumId)
    .map((payload) => toAlbumFromFavorite(payload));
};

export const libraryAlbumsService = {
  async fetchCachedAlbums(spotifyUserId: string): Promise<Album[]> {
    const { data } = await backendClient.get<CachedAlbumsResponse>('/library/albums', {
      params: { spotifyUserId }
    });
    return deserializeFavoriteAlbums(data?.albums);
  },

  async syncAlbums(options: {
    spotifyUserId: string;
    accessToken: string;
    pageLimit?: number;
  }): Promise<{ summary: SyncAlbumsResponse; albums: Album[] }> {
    const headers = {
      Authorization: `Bearer ${options.accessToken}`
    };

    const { data } = await backendClient.post<SyncAlbumsResponse>(
      '/library/albums/sync',
      {
        spotifyUserId: options.spotifyUserId,
        pageLimit: options.pageLimit
      },
      { headers }
    );

    const albums = deserializeFavoriteAlbums(data?.albums);
    return {
      summary: data,
      albums
    };
  },

  async fetchAlbumsPage(options: {
    spotifyUserId: string;
    accessToken: string;
    after?: string | null;
    limit?: number;
    sessionId?: string | null;
  }): Promise<FetchAlbumsPageResponse> {
    const trimmedToken = options.accessToken.trim();
    const headers = {
      Authorization: `Bearer ${trimmedToken}`
    };

    const params: Record<string, string | number> = {
      spotifyUserId: options.spotifyUserId
    };

    if (typeof options.after === 'string' && options.after.trim().length > 0) {
      params.after = options.after;
    }

    if (typeof options.sessionId === 'string' && options.sessionId.trim().length > 0) {
      params.sessionId = options.sessionId;
    }

    if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
      const normalizedLimit = Math.trunc(options.limit);
      if (normalizedLimit > 0) {
        params.limit = Math.min(normalizedLimit, 50);
      }
    }

    const { data } = await backendClient.get<AlbumsPageResponsePayload>('/library/albums/page', {
      params,
      headers
    });

    const albums = deserializeFavoriteAlbums(data?.albums);
    const nextCursor =
      typeof data?.nextCursor === 'string' && data.nextCursor.trim().length > 0
        ? data.nextCursor
        : null;
    const overlap = Boolean(data?.overlap);
    const totalFetched =
      typeof data?.totalFetched === 'number' && Number.isFinite(data.totalFetched)
        ? data.totalFetched
        : albums.length;
    const sessionId =
      typeof data?.sessionId === 'string' && data.sessionId.trim().length > 0
        ? data.sessionId
        : null;
    const commit = Boolean(data?.commit);
    const persistedCount =
      typeof data?.persistedCount === 'number' && Number.isFinite(data.persistedCount)
        ? data.persistedCount
        : 0;
    const totalPersisted =
      typeof data?.totalPersisted === 'number' && Number.isFinite(data.totalPersisted)
        ? data.totalPersisted
        : null;
    const latestAlbumId =
      typeof data?.latestAlbumId === 'string' && data.latestAlbumId.trim().length > 0
        ? data.latestAlbumId.trim()
        : null;
    const latestAlbumAddedAt =
      typeof data?.latestAlbumAddedAt === 'string' && data.latestAlbumAddedAt.trim().length > 0
        ? data.latestAlbumAddedAt.trim()
        : null;

    return {
      albums,
      nextCursor,
      overlap,
      totalFetched,
      sessionId,
      commit,
      persistedCount,
      totalPersisted,
      latestAlbumId,
      latestAlbumAddedAt
    };
  }
};

export default libraryAlbumsService;
